import { API_BASE, getToken } from "./api.js";
import { tuneOpusForVoice } from "./sdp.js";

/**
 * Live commentary through a server, instead of to each listener directly.
 *
 * The mesh this replaces had the scorer hold one peer connection per listener.
 * That is why listeners were capped at ten, why the scorer's uplink grew with
 * the audience, and why two phones on mobile data often could not connect at
 * all — they were trying to find each other behind carrier NAT.
 *
 * Here the scorer publishes once to Cloudflare and every listener pulls from
 * it. The scorer's phone sends one stream no matter who is watching, and both
 * sides are talking to a public server rather than to each other, so NAT stops
 * mattering.
 *
 * It also makes the signalling smaller rather than larger. There is no offer
 * to relay per listener and no ICE to forward — the room only has to carry one
 * fact: where the scorer's stream is. Everything else is an ordinary HTTPS
 * request to our own worker, which holds the application token.
 */

const TRACK = "commentary";

async function post<T>(path: string, body?: unknown, method = "POST"): Promise<T | null> {
  try {
    const token = getToken();
    const r = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

/** Is the SFU configured at all? Asked once, so the UI can say why not. */
export async function available(): Promise<boolean> {
  const r = await post<{ sfu: boolean }>("/api/v1/rtc/mode", undefined, "GET");
  return r?.sfu === true;
}

interface SessionDescription { type: string; sdp: string }

/**
 * Wait for ICE to settle before handing the SDP over.
 *
 * Realtime wants a description it can act on immediately rather than a stream
 * of trickled candidates, and this is a single hop to a public server, so
 * gathering finishes quickly. Bounded anyway: a network that never completes
 * gathering should degrade to what we have, not hang for ever.
 */
function gathered(pc: RTCPeerConnection, ms = 4000): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); pc.removeEventListener("icegatheringstatechange", check); resolve(); };
    const check = () => { if (pc.iceGatheringState === "complete") done(); };
    const timer = setTimeout(done, ms);
    pc.addEventListener("icegatheringstatechange", check);
  });
}

/** Where a published stream lives, so a listener can ask for it. */
export interface Publication { sessionId: string; trackName: string }

/* ── the scorer ───────────────────────────────────────────────────────────── */

export class Publisher {
  private pc: RTCPeerConnection | null = null;
  private track: MediaStreamTrack | null = null;
  private pub: Publication | null = null;

  /** Publish the microphone. Returns where it landed, or null if it did not. */
  async publish(stream: MediaStream): Promise<Publication | null> {
    const session = await post<{ sessionId: string }>("/api/v1/rtc/session");
    if (!session?.sessionId) return null;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
      bundlePolicy: "max-bundle",
    });
    this.pc = pc;

    this.track = stream.getAudioTracks()[0] ?? null;
    if (!this.track) return null;
    // Silent until the button is held. The connection stays up between presses
    // so a press costs nothing but flipping this flag.
    this.track.enabled = false;

    const tx = pc.addTransceiver(this.track, { direction: "sendonly" });

    const offer = await pc.createOffer();
    const sdp = tuneOpusForVoice(offer.sdp ?? "");
    await pc.setLocalDescription({ type: "offer", sdp });
    await gathered(pc);

    const res = await post<{ sessionDescription?: SessionDescription; errorDescription?: string }>(
      `/api/v1/rtc/session/${session.sessionId}/tracks`,
      {
        sessionDescription: { type: "offer", sdp: pc.localDescription?.sdp ?? sdp },
        tracks: [{ location: "local", mid: tx.mid, trackName: TRACK }],
      },
    );
    if (!res?.sessionDescription) {
      console.error("sfu: publish refused", res?.errorDescription);
      this.close();
      return null;
    }
    await pc.setRemoteDescription(res.sessionDescription as RTCSessionDescriptionInit);

    this.pub = { sessionId: session.sessionId, trackName: TRACK };
    return this.pub;
  }

  /** Hold to talk. */
  setTalking(on: boolean): void {
    if (this.track) this.track.enabled = on;
  }

  get publication(): Publication | null { return this.pub; }

  get connected(): boolean {
    return this.pc?.connectionState === "connected";
  }

  close(): void {
    if (this.track) this.track.enabled = false;
    try { this.pc?.close(); } catch { /* already closed */ }
    this.pc = null;
    this.pub = null;
  }
}

/* ── a listener ───────────────────────────────────────────────────────────── */

export type SfuState = "off" | "connecting" | "live" | "failed";

export class Subscriber {
  private pc: RTCPeerConnection | null = null;
  private el: HTMLAudioElement;
  private onState: (s: SfuState, detail?: string) => void;

  constructor(el: HTMLAudioElement, onState: (s: SfuState, d?: string) => void) {
    this.el = el;
    this.onState = onState;
  }

  /** Pull the scorer's stream. Must be called from a tap: autoplay rules. */
  async listen(pub: Publication): Promise<void> {
    this.close();
    this.onState("connecting");

    const session = await post<{ sessionId: string }>("/api/v1/rtc/session");
    if (!session?.sessionId) return this.onState("failed", "Could not start listening.");

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
      bundlePolicy: "max-bundle",
    });
    this.pc = pc;

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      if (!stream) return;
      this.el.srcObject = stream;
      void this.el.play().catch(() => this.onState("failed", "Tap Listen again to allow sound."));
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") this.onState("live");
      else if (pc.connectionState === "failed") this.onState("failed", "Lost the connection.");
    };

    // Asking for a remote track makes the SFU the offerer, which is the
    // opposite way round from publishing.
    const res = await post<{ sessionDescription?: SessionDescription; errorDescription?: string }>(
      `/api/v1/rtc/session/${session.sessionId}/tracks`,
      {
        tracks: [{
          location: "remote",
          sessionId: pub.sessionId,
          trackName: pub.trackName,
        }],
      },
    );
    if (!res?.sessionDescription) {
      console.error("sfu: pull refused", res?.errorDescription);
      return this.onState("failed", "The commentary stream is not available.");
    }

    await pc.setRemoteDescription(res.sessionDescription as RTCSessionDescriptionInit);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await gathered(pc);

    const ok = await post<unknown>(
      `/api/v1/rtc/session/${session.sessionId}/renegotiate`,
      { sessionDescription: { type: "answer", sdp: pc.localDescription?.sdp ?? answer.sdp } },
      "PUT",
    );
    if (ok === null) this.onState("failed", "Could not finish connecting.");
  }

  close(): void {
    try { this.pc?.close(); } catch { /* already closed */ }
    this.pc = null;
    this.el.srcObject = null;
    this.onState("off");
  }
}
